import type { Service } from '../types';

const DELIVERY_DISCLAIMERS = [
  'The project officially commences upon confirmation of payment and receipt and validation of the required onboarding materials',
  'Project timelines may be shortened when feedback is submitted earlier, and may be extended if feedback exceeds 5 business days. Updates will be clearly visible at all times on your private project dashboard.',
  'Scope changes require written approval and may affect the project timeline',
  'Rush delivery is available upon request (subject to availability; additional fees apply). Please contact us before purchasing if required.',
];

export const services: Service[] = [
  {
    id: 'design-audit',
    slug: 'website-design-audit',
    title: 'Website Design Audit',
    tagline: 'Know exactly what is holding your website back',
    description: 'A focused, expert review of your website design, usability, and visual hierarchy. You receive a structured improvement report with clear, actionable recommendations you can hand to any developer or implement yourself.',
    icon: 'Search',
    price: '$1,300',
    priceNote: 'One time. No subscriptions. Final purchase. Non refundable.',
    deliverable: 'Structured improvement report with prioritized design recommendations',
    forWho: 'Business owners who have a website that feels outdated, confusing, or underperforming but are not sure why',
    serviceCategory: 'audit',
    painPoints: [
      'I do not know what makes a good website versus a bad one',
      'My site exists but it does not do anything for my business',
      'I want an expert opinion before spending money on a redesign',
    ],
    process: [
      { step: 1, title: 'Submit Your URL', description: 'Share your website link and tell us about your business goals' },
      { step: 2, title: 'We Analyze Everything', description: 'Layout, navigation, mobile experience, visual hierarchy, calls to action, and user flow' },
      { step: 3, title: 'Get Your Report', description: 'A structured document with what works, what does not, and exactly what to fix first' },
    ],
    features: [
      'Visual hierarchy review',
      'Layout and spacing evaluation',
      'Accessibility overview',
      'Conversion friction analysis',
      'Mobile optimization review',
      'Structured improvement report',
    ],
    inScope: [
      'Visual hierarchy review',
      'Layout and spacing evaluation',
      'Accessibility overview',
      'Conversion friction analysis',
      'Mobile optimization review',
      'Structured improvement report',
    ],
    outOfScope: [
      'Redesign',
      'Development fixes',
      'Copywriting edits',
      'Technical SEO review',
    ],
    conditions: [
      'Estimated project timeline: 5 business days',
      'Excludes up to 7 business days for intake and validation',
      'Includes annotated feedback document',
      'All purchases are non-refundable',
    ],
    extras: [],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 5,
      estimatedDays: 5,
      phases: [
        { days: 5, label: 'Audit execution and delivery', isClientWindow: false },
      ],
      revisionRounds: null,
      note: 'Execution is completed within 5 business days once access and documentation are validated. No client review window is required.',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'How long does the audit take?', answer: 'You will receive your report within 5 business days from project start, once access and documentation have been validated.' },
      { question: 'Will you also fix the issues?', answer: 'The audit identifies what needs fixing. If you want us to implement the changes, our [Fully Custom Website Build](/services/fully-custom-website-build) service is the next step.' },
      { question: 'Can I use this report with my own developer?', answer: 'Absolutely. The report is written to be clear and actionable for any developer or designer.' },
    ],
  },
  {
    id: 'seo-audit',
    slug: 'seo-reality-check',
    title: 'SEO Reality Check (Full Website Audit)',
    tagline: 'Find out why Google is not sending you traffic',
    description: 'A technical and on-page SEO analysis with an AEO review that uncovers exactly why your website is invisible to search engines. You receive a clear, actionable roadmap with fixes ranked by impact so you know what to fix first.',
    icon: 'BarChart3',
    price: '$750',
    priceNote: 'One time. No subscriptions. Final purchase. Non refundable.',
    deliverable: 'Actionable roadmap document with SEO fixes ranked by impact',
    forWho: 'Business owners who have a website but get little to no organic traffic from Google',
    serviceCategory: 'audit',
    painPoints: [
      'Is my website even being indexed by search engines and AI tools?',
      'SEO feels like a mystery and I do not know where to begin',
      'I was told my site is SEO friendly but I get zero traffic',
    ],
    process: [
      { step: 1, title: 'Share Your Site', description: 'Share your URL and the audience you want your site to reach' },
      { step: 2, title: 'Deep Technical Review', description: 'We check indexing, page speed, meta tags, content structure, internal linking, and more' },
      { step: 3, title: 'Actionable Roadmap', description: 'A document organized by priority so you tackle the highest impact fixes first' },
    ],
    features: [
      'Technical SEO review',
      'On-page structural review',
      'AEO readability review',
      'Performance and crawlability checks',
      'Meta and structured data review',
      'Prioritized action roadmap',
    ],
    inScope: [
      'Indexing status',
      'Google Search Console configuration',
      'Sitemap validation',
      'Crawlability checks',
      'Performance red flags',
      'Meta configuration review',
      'Basic structured data review',
      'AEO readability review',
      'Heading hierarchy (H1 to H3 logic)',
      'Page structure clarity',
      'Internal linking logic',
      'Content architecture gaps',
      'Structural SEO alignment',
      'Prioritized action roadmap',
    ],
    outOfScope: [
      'Implementation of fixes',
      'Keyword research',
      'Competitor analysis',
      'Content rewriting',
      'Backlink outreach',
      'Ongoing SEO management',
      'Live calls, walkthroughs, or consultation',
    ],
    conditions: [
      'Estimated project timeline: 5 business days',
      'Excludes up to 7 business days for intake and validation',
      'No revision rounds',
      'All purchases are non-refundable',
    ],
    extras: [
      { name: 'SEO rewriting', price: '$400/page', description: 'Full content rewrite optimized for the keywords identified in the audit. Includes keyword integration, heading restructure, meta descriptions, and internal linking recommendations. This goes beyond the audit report by implementing the changes rather than just identifying them.' },
    ],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 5,
      estimatedDays: 5,
      phases: [
        { days: 5, label: 'Full audit execution and delivery', isClientWindow: false },
      ],
      revisionRounds: null,
      note: 'Full audit execution is completed within 5 business days',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'I know nothing about SEO. Will I understand the report?', answer: 'Yes. Every recommendation is written in plain language with step by step instructions.' },
      { question: 'Will this guarantee I rank on Google?', answer: 'No audit can guarantee rankings. What we guarantee is a clear roadmap of what to fix first to give your site the best chance of ranking.' },
      { question: 'How is this different from free SEO tools?', answer: 'Free tools give you scores and vague alerts, and most only analyze one page at a time. We analyze your entire website, including SEO and AEO visibility, and provide context, priorities, and a clear roadmap tailored to your business.' },
    ],
  },
  {
    id: 'content-writing',
    slug: 'website-content-writing',
    title: 'Website Content Writing & SEO Optimization',
    tagline: 'Professional content written for your audience and search engines',
    description: 'We write your website content from scratch, fully optimized for SEO. You receive ready-to-publish content built around keyword research.',
    icon: 'FileText',
    price: '$400/page',
    priceNote: 'Per page. Final purchase. Non refundable.',
    deliverable: 'Fully written, SEO-optimized, ready to publish content',
    forWho: 'Business owners who need professional website content written for them, whether starting fresh or replacing content that is not performing',
    serviceCategory: 'audit',
    painPoints: [
      'I do not have website content and I do not know how to write it',
      'I need a professional to write content that actually converts visitors',
      'I need content that ranks on Google, not just fills space',
    ],
    process: [
      { step: 1, title: 'Tell Us Your Goals', description: 'Share your business, target audience, and what you want visitors to do on each page' },
      { step: 2, title: 'We Research and Write', description: 'Keyword research, competitor analysis, and professional SEO content writing from scratch' },
      { step: 3, title: 'Review Your Content', description: 'Receive a clean document with your written content, headings, meta descriptions, and implementation notes' },
    ],
    features: [
      'Keyword research per page',
      'Professional SEO content writing',
      'Meta descriptions and heading structure',
      'Tone alignment to brand',
      'Internal linking suggestions',
      'Implementation guide included',
    ],
    inScope: [
      'Keyword research per page',
      'Professional SEO content writing',
      'Meta descriptions and heading structure',
      'Tone alignment to brand voice',
      'Internal linking suggestions',
      'Implementation guide',
    ],
    outOfScope: [
      'Design changes',
      'Development work',
      'Technical SEO fixes',
      'Ongoing content production',
      'Publishing or implementation',
    ],
    conditions: [
      'Estimated timeline: 5 business days for the first page, +3 per additional page',
      'Excludes up to 7 business days for intake and validation',
      'Document format (Word or Google Doc)',
      'All purchases are non-refundable',
    ],
    extras: [],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 5,
      estimatedDays: 5,
      phases: [
        { days: 5, label: 'First page delivery', isClientWindow: false },
        { days: 3, label: 'Per additional page', isClientWindow: false },
      ],
      revisionRounds: null,
      note: 'Client review is not required before delivery. One final document is delivered for implementation.',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'Do you also publish the content on my site?', answer: 'This service delivers the written content in a document. If you need it implemented on your website, pair it with our [Fully Custom Website Build](/services/fully-custom-website-build) service.' },
      { question: 'How many pages can you write?', answer: 'The price is $400 per page. You choose how many pages you need and purchase each one individually.' },
      { question: 'How do you make sure the content fits my business?', answer: 'We start by understanding your brand, audience, and goals before writing anything.' },
    ],
  },
  {
    id: 'direction-sprint',
    slug: 'two-strategic-homepage-prototypes',
    title: 'Two Strategic Homepage Prototypes',
    tagline: 'See real, clickable designs before you commit to a full build',
    description: 'Two distinct homepage directions built as fully interactive prototypes. Not static PDF. Not Figma files. Real pages you can interact with. Pick one, refine it, or walk away with total clarity on what you actually want before committing to a full build.',
    icon: 'Compass',
    price: '$1,950',
    priceNote: 'Credited toward full website build if project proceeds within 30 days. Final purchase. Non refundable.',
    deliverable: 'Two clickable website directions with two revision rounds on selected direction',
    forWho: 'Business owners who have been burned before, are nervous about committing to a full build, or want to see real work before investing further',
    serviceCategory: 'prototype',
    painPoints: [
      'I am afraid of paying a lot and not liking the result',
      'I already paid once and the design was nothing like what I asked for',
      'I want to see something real before I invest in a full build',
    ],
    process: [
      { step: 1, title: 'Share Your Vision', description: 'Tell us about your business, your style preferences, and any sites you admire' },
      { step: 2, title: 'We Build Two Directions', description: 'Two distinct homepage prototypes you can click through and experience, not static images' },
      { step: 3, title: 'Pick and Refine', description: 'Choose the direction that resonates. We refine it with two revision rounds included.' },
      { step: 4, title: 'Decide Your Next Step', description: 'Move forward with a full build (Sprint cost credited) or walk away with complete clarity' },
    ],
    features: [
      'Two distinct clickable website directions',
      'UX structure and visual style concept',
      'Homepage prototype for each direction',
      'Decision framework included',
      'Two revision rounds on selected direction',
      'Credited toward full build within 30 days',
    ],
    inScope: [
      'Two fully functional homepage prototypes',
      'Two distinct structure and positioning directions',
      'UX structure and layout logic',
      'Visual style concept (homepage level only)',
      'SEO-aware layout hierarchy',
      'Two revision rounds on selected direction',
      'Credited toward full build if you proceed',
    ],
    outOfScope: [
      'Full website design or development',
      'Copywriting beyond structural concept messaging',
      'SEO implementation',
      'CRM integrations',
      'Custom databases',
      'Automation or backend systems',
      'Domain registration and renewal fees (must be paid directly by client)',
    ],
    conditions: [
      'Estimated project timeline: 19 business days',
      'Excludes up to 7 business days for intake and validation',
      'No Figma only phase',
      'No PDF mockups',
      'No static screenshots',
      'Credited toward full build if project proceeds within 30 days',
      '2 revision rounds',
      'All purchases are non-refundable',
    ],
    extras: [
      { name: 'SEO writing', price: '$400/page', description: 'Professional content creation optimized for target keywords and search intent. Includes keyword research, competitor analysis, and content structured for both readers and search engines. Separate from the design work included in the base service.' },
      { name: 'Fully Custom Website Build', price: '$6,900', description: 'Full website build based on your provided content. Includes custom design, development, core SEO structure, DNS setup, Search Console configuration, and full deployment. Your prototype investment is credited toward the project if you proceed within 30 days.' },
      { name: 'Complete Website Build (Content + Website)', price: '$11,000', description: 'Complete website creation including content strategy, writing, positioning, custom design, development, technical SEO setup, and full deployment. Your prototype investment is credited toward the project if you proceed within 30 days.' },
    ],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 19,
      estimatedDays: 19,
      phases: [
        { days: 5, label: 'Two homepage directions delivered', isClientWindow: false },
        { days: 5, label: 'Client review and consolidated feedback', isClientWindow: true },
        { days: 5, label: 'Refinement of selected direction', isClientWindow: false },
        { days: 3, label: 'Final confirmation and approval', isClientWindow: true },
        { days: 1, label: 'Finalization', isClientWindow: false },
      ],
      revisionRounds: 2,
      note: 'Timeline includes both internal build phases and client review windows',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'What if I do not like either direction?', answer: 'You walk away with clarity on what you do not want and what does not resonate. That alone saves you from investing in the wrong direction later.' },
      { question: 'Are these real pages or just images?', answer: 'Real, clickable pages you can interact with. No PDFs, no static screenshots, no Figma files. You experience the actual website.' },
      { question: 'What happens if I want to proceed with a full build?', answer: 'The full Sprint cost is credited toward your website development or full creation project if you proceed within 30 days.' },
    ],
  },
  {
    id: 'website-development',
    slug: 'fully-custom-website-build',
    title: 'Fully Custom Website Build',
    tagline: 'Already have your content? We will turn it into a fully custom, professionally built website.',
    description: 'Full website build based on your provided content. Includes custom design, development, core SEO structure, DNS setup, Search Console configuration, and full deployment.',
    icon: 'Code2',
    price: '$6,900',
    priceNote: 'Up to 5 core pages. Additional pages available as an upgrade. Final purchase. Non refundable.',
    deliverable: 'Fully designed and deployed custom website',
    forWho: 'Business owners who have their content ready but need it built into a website that actually works well on every device',
    serviceCategory: 'build',
    painPoints: [
      'My current site is slow, broken, or looks terrible on mobile',
      'I want something unique, not a template I have seen on ten other websites',
      'I have the content but no clear vision of how it should come together',
    ],
    process: [
      { step: 1, title: 'Send Your Content', description: 'Share your text, images, and any specific requirements' },
      { step: 2, title: 'We Design and Build', description: 'Custom UX and UI design, clean code, fast performance, mobile responsive, SEO foundations included' },
      { step: 3, title: 'Review Your Website', description: 'You get a live link to review, test, and request changes. Three revision rounds included.' },
      { step: 4, title: 'Go Live', description: 'Final updates, deployment, analytics setup, and Search Console configuration' },
    ],
    features: [
      'Up to 5 core pages',
      'Custom UX and UI design',
      'Responsive development',
      'Basic on page SEO structure',
      'Analytics and Search Console setup',
      'Three revision rounds and post launch support',
    ],
    inScope: [
      'Up to 5 unique page layouts (each distinct layout counts as one page, including landing pages)',
      'Custom design aligned with your positioning',
      'Responsive development',
      'Blog setup (optional)',
      'Contact form integration (optional)',
      'Core on-page SEO structure',
      'Technical SEO implementation',
      'Technical AEO implementation',
      'Google Search Console setup',
      'Sitemap submission',
      'Full DNS configuration and deployment',
    ],
    outOfScope: [
      'Content writing or creation',
      'Brand strategy',
      'CRM integrations',
      'Client data systems',
      'API integrations',
      'Custom database builds',
      'Automation workflows',
      'Complex publishing workflows',
      'CMS (available as add-on)',
      'Domain registration and renewal fees (must be paid directly by client)',
      'Ongoing maintenance beyond handover',
    ],
    conditions: [
      'Estimated project timeline: 29 business days',
      'Excludes up to 7 business days for intake and validation',
      'No Figma only phase',
      'No PDF mockups',
      'No static screenshots',
      '3 revision rounds',
      'All purchases are non-refundable',
    ],
    extras: [
      { name: 'Additional pages', price: '+$400/page', description: 'Extra pages beyond the 5 core pages included in the base service. Each additional page includes custom design, responsive development, and basic on-page SEO structure matching the style of your core pages.' },
      { name: 'CMS integration', price: '+$3,000', description: 'Adds a content management layer so you can edit text, images, and pages without a developer. Includes CMS setup, content modeling, and a walkthrough of how to use it. The base service delivers a static site; this extra makes it editable.' },
      { name: 'Booking or payment integration', price: '+$1,200', description: 'Connects your website to an online scheduling or payment processing platform such as Calendly, Stripe, or Square. Includes the embed or API integration, styling to match your site, and testing. Not included in the base service.' },
    ],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 29,
      estimatedDays: 29,
      phases: [
        { days: 5, label: 'Two homepage directions delivered', isClientWindow: false },
        { days: 5, label: 'Client direction selection and feedback', isClientWindow: true },
        { days: 5, label: 'Full site structural build', isClientWindow: false },
        { days: 5, label: 'Client review window', isClientWindow: true },
        { days: 3, label: 'Revision implementation', isClientWindow: false },
        { days: 3, label: 'Final confirmation', isClientWindow: true },
        { days: 3, label: 'Deployment and configuration', isClientWindow: false },
      ],
      revisionRounds: 3,
      note: 'Timeline includes both internal build phases and client review windows',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'Can you write the content for me too?', answer: 'This service is development only. For content, pair it with our [Website Content Writing & SEO Optimization](/services/website-content-writing) service or choose our [Complete Website Build](/services/complete-website-build) package.' },
      { question: 'What platform do you build on?', answer: 'We choose the best technology for your needs. We are not locked into one platform.' },
      { question: 'Will I be able to edit the site myself after launch?', answer: 'The base service delivers a fully built website, and you will be able to monitor its performance through Google Analytics, which we set up for you. If you need to update content regularly, a CMS integration can be added as an upgrade.' },
    ],
  },
  {
    id: 'full-creation',
    slug: 'complete-website-build',
    title: 'Complete Website Build (Content + Website)',
    tagline: 'From blank page to live business. We handle everything.',
    description: 'Complete website creation including content strategy, writing, positioning, custom design, development, technical SEO setup, and full deployment.',
    icon: 'Rocket',
    price: '$11,000',
    priceNote: 'All inclusive. Strategy, content, design, and development. Final purchase. Non refundable.',
    deliverable: 'Fully designed, content-optimized, and deployed website',
    forWho: 'Business owners who want everything handled from start to finish without the stress of coordinating multiple people or tools',
    serviceCategory: 'build',
    painPoints: [
      'I do not know where to start and I just want it done properly',
      'I keep going in circles trying to figure this out on my own',
      'I am stuck tweaking instead of launching my business online',
    ],
    process: [
      { step: 1, title: 'Discovery and Strategy', description: 'We learn about your business, goals, audience, and preferences. Site structure and SEO keywords defined.' },
      { step: 2, title: 'Content Creation', description: 'Full website copywriting (up to 5 pages) with SEO keyword alignment and strategic messaging' },
      { step: 3, title: 'Design and Build', description: 'Custom design and professional development based on the approved strategy and content' },
      { step: 4, title: 'Review and Launch', description: 'You review the live website, we refine through three revision rounds, and launch when ready' },
    ],
    features: [
      'Everything in Fully Custom Website Build',
      'Strategic messaging',
      'Full website copywriting (up to 5 pages)',
      'SEO keyword alignment',
      'Three revision rounds',
      'Post launch support and training',
    ],
    inScope: [
      'Content strategy and structuring',
      'Positioning and messaging clarity',
      'Website copywriting (up to 5 core pages)',
      'Site architecture planning',
      'Custom design',
      'Development',
      'Technical SEO implementation',
      'Technical AEO implementation',
      'Deployment and configuration',
      'Everything included in Fully Custom Website Build',
    ],
    scopeNote: 'Outcome: From unclear to launched, with content and structure done for you.',
    outOfScope: [
      'CMS customization (available as add-on)',
      'CRM systems',
      'API integrations',
      'Custom databases',
      'Automation systems',
      'Complex client data workflows',
      'Ongoing SEO campaigns',
      'Blog content beyond core pages',
      'Ongoing maintenance beyond handover',
      'Domain registration and renewal fees (paid directly by client)',
      'Regulatory compliance certification',
    ],
    outOfScopeNote: 'Those fall under Strategic Architecture Build.',
    conditions: [
      'Estimated project timeline: 44 business days',
      'Excludes up to 7 business days for intake and validation',
      'No Figma only phase',
      'No PDF mockups',
      'No static screenshots',
      '3 revision rounds',
      'All purchases are non-refundable',
    ],
    extras: [
      { name: 'CMS integration', price: '+$3,000', description: 'Adds a content management layer so you can edit text, images, and pages without a developer. Includes CMS setup, content modeling, and a walkthrough of how to use it. The base service delivers a static site; this extra makes it editable.' },
      { name: 'Booking or payment integration', price: '+$1,200', description: 'Connects your website to an online scheduling or payment processing platform such as Calendly, Stripe, or Square. Includes the embed or API integration, styling to match your site, and testing. Not included in the base service.' },
    ],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 44,
      estimatedDays: 44,
      phases: [
        { days: 5, label: 'Two homepage directions delivered', isClientWindow: false },
        { days: 5, label: 'Direction selection and feedback', isClientWindow: true },
        { days: 20, label: 'Content creation and structural build', isClientWindow: false },
        { days: 5, label: 'Client review', isClientWindow: true },
        { days: 3, label: 'Revision implementation', isClientWindow: false },
        { days: 3, label: 'Final confirmation', isClientWindow: true },
        { days: 3, label: 'Deployment', isClientWindow: false },
      ],
      revisionRounds: 3,
      note: 'Timeline includes both internal build phases and client review windows',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'How long does the full process take?', answer: 'The estimated project timeline is 44 business days. This includes 3 revision rounds and approximately 13 business days allocated for client review across the process. This excludes up to 7 business days for intake and validation.' },
      { question: 'What if I already have some content?', answer: 'Great. We will review it, optimize it for SEO, and integrate it into the design.' },
      { question: 'Do I own the final website?', answer: 'Yes. All final approved deliverables, including designs, content, and assets, belong to you upon completion. The Company retains rights over any concepts or directions not selected during the process.' },
    ],
  },
  {
    id: 'architecture-build',
    slug: 'strategic-architecture-build',
    title: 'Strategic Architecture Build',
    tagline: 'Custom system architecture for complex business needs',
    description: 'End-to-end system architecture and development for businesses with complex workflows, integrations, and data requirements. Scope defined after technical evaluation. Application required before proposal.',
    icon: 'Blocks',
    price: '$14,000+',
    priceNote: 'Final scope and pricing confirmed after technical evaluation. Final purchase. Non refundable.',
    deliverable: 'Fully deployed custom system with integrations, automation, and documentation',
    forWho: 'Businesses with complex operational needs that require CRM integration, API connections, automation logic, custom databases, or security-sensitive workflows',
    serviceCategory: 'build',
    painPoints: [
      'My business tools do not talk to each other and everything is manual',
      'I need a system that handles sensitive data securely and at scale',
      'Off the shelf software does not fit my workflow',
    ],
    process: [
      { step: 1, title: 'Direction and Alignment', description: 'High-level architecture alignment and scope definition. 5 business days.' },
      { step: 2, title: 'Client Validation', description: 'You review and validate the proposed direction. Up to 5 business days.' },
      { step: 3, title: 'System Architecture', description: 'Detailed system architecture, data modeling, and integration mapping. 10 business days.' },
      { step: 4, title: 'Client Approval', description: 'Architecture review and sign-off before development begins. Up to 5 business days.' },
      { step: 5, title: 'Development and Integrations', description: 'Full build including APIs, automation, database, and third-party integrations. 20 business days.' },
      { step: 6, title: 'Client Review', description: 'Review the working system and provide feedback. Up to 5 business days.' },
      { step: 7, title: 'Revision Implementation', description: 'Refinements based on your feedback. 3 business days.' },
      { step: 8, title: 'Deployment and Configuration', description: 'Final deployment, environment configuration, and handover. 3 business days.' },
    ],
    features: [
      'CRM integration and lifecycle flows',
      'API connections between tools',
      'Automation logic (notifications, workflow triggers)',
      'Client data systems',
      'Custom database requirements',
      'Security-sensitive workflow handling',
      'Full deployment and configuration',
      'Post launch support',
    ],
    scopeLabel: 'Typical scope',
    inScope: [
      'Full A\u2013Z Build (Content + Website)',
      'CRM integration and lifecycle flows',
      'API connections between tools',
      'Automation logic (notifications, workflow triggers)',
      'Client data systems',
      'Custom database requirements',
      'Security-sensitive workflows',
      'Regulated or sensitive data environments (healthcare, financial, etc.)',
    ],
    scopeNote: 'Scope defined after technical evaluation. Application required before proposal.',
    outOfScope: [
      'Ongoing maintenance beyond handover',
      'Third-party subscription costs',
      'Regulatory compliance certification',
      'Domain registration and renewal fees (must be paid directly by client)',
    ],
    conditions: [
      'Estimated timeline: 56 to 65 business days',
      'Excludes up to 7 business days for intake and validation',
      'Final timeline confirmed after scope approval',
      'Application required before proposal',
      'Scope defined after technical evaluation',
      '3 revision rounds',
      'All purchases are non-refundable',
    ],
    extras: [],
    timeline: {
      firstDeliveryDays: 5,
      maxDeliveryDays: 56,
      estimatedDays: 56,
      estimatedDaysRange: '56\u201365',
      phases: [
        { days: 5, label: 'Direction and high-level architecture alignment', isClientWindow: false },
        { days: 5, label: 'Client validation', isClientWindow: true },
        { days: 10, label: 'System architecture', isClientWindow: false },
        { days: 5, label: 'Client approval', isClientWindow: true },
        { days: 20, label: 'Development and integrations', isClientWindow: false },
        { days: 5, label: 'Client review', isClientWindow: true },
        { days: 3, label: 'Revision implementation', isClientWindow: false },
        { days: 3, label: 'Deployment and configuration', isClientWindow: false },
      ],
      revisionRounds: 3,
      note: 'Timeline includes both internal build phases and client review windows. Final timeline will depend on project complexity.',
      deliveryDisclaimers: DELIVERY_DISCLAIMERS,
    },
    faq: [
      { question: 'Do I need to apply before getting a proposal?', answer: 'Yes. Due to the complexity and custom nature of this service, we require an application so we can evaluate your needs before providing a scope and proposal.' },
      { question: 'What industries do you work with?', answer: 'We work with any industry that has complex operational requirements. This includes financial services, real estate, logistics, and more. The common thread is a need for secure, automated, integrated systems.' },
      { question: 'What types of projects qualify for this service?', answer: 'This service is designed for businesses or website projects with complex operational requirements, such as multi-system integrations, automated workflows, secure data handling, or custom internal tools. If your needs go beyond a standard website, this is the right fit.' },
    ],
  },
];

export function getServiceBySlug(slug: string): Service | undefined {
  return services.find(s => s.slug === slug);
}

export function getServiceById(id: string): Service | undefined {
  return services.find(s => s.id === id);
}

export function getPriceInCents(slug: string): number | undefined {
  const service = getServiceBySlug(slug);
  if (!service) return undefined;
  const raw = service.price.replace(/[^0-9]/g, '');
  const num = parseInt(raw, 10);
  return isNaN(num) ? undefined : num * 100;
}

export function getLowestPriceService(): Service {
  return services.reduce((lowest, s) => {
    const rawLowest = parseInt(lowest.price.replace(/[^0-9]/g, ''), 10) || Infinity;
    const rawCurrent = parseInt(s.price.replace(/[^0-9]/g, ''), 10) || Infinity;
    return rawCurrent < rawLowest ? s : lowest;
  });
}

export function buildServiceNameMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const s of services) {
    map[s.slug] = s.title;
  }
  return map;
}

export const INTAKE_VALIDATION_DAYS = 7;
export const INTAKE_FORM_DEADLINE_DAYS = 3;
